import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { AlertCircle, Sparkles, CreditCard, Calendar } from "lucide-react";

const DEMO_BOOKING_URL = "https://viral.partners/umow-demo";

export function TrialBanner() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [location, setLocation] = useLocation();

  if (location === "/trial" || location === "/register") return null;

  const trialEndsAt = user?.trialEndsAt;
  const subscriptionActive = user?.subscriptionActive;
  const trialPlus = user?.trialPlus;

  // Don't show if no trial or already subscribed
  if (!trialEndsAt || subscriptionActive) return null;

  // Trial+ users — show next Friday as deadline
  if (trialPlus) {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0=Sun, 5=Fri
    const daysUntilFriday = (5 - dayOfWeek + 7) % 7 || 7;
    const nextFriday = new Date(now);
    nextFriday.setDate(now.getDate() + daysUntilFriday);
    const fridayStr = nextFriday.toLocaleDateString(i18n.language === "pl" ? "pl-PL" : "en-US", {
      day: "numeric",
      month: "long",
    });

    return (
      <div className="w-full bg-[#1a1c22] border-b border-white/[0.08] text-white text-center py-2 px-4 text-sm flex items-center justify-center gap-3 z-50">
        <Sparkles className="w-4 h-4 text-emerald-400 flex-shrink-0" />
        <span>
          {t("trialBanner.trialPlusPrefix")}{" "}
          <span className="font-bold text-emerald-400">Trial+</span>{" "}
          {t("trialBanner.trialPlusUntil")}{" "}
          <span className="font-bold text-emerald-400">{fridayStr}</span>
        </span>
        <button
          onClick={() => setLocation("/settings")}
          className="ml-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 px-3 py-0.5 rounded text-xs font-semibold transition-colors"
        >
          <CreditCard className="w-3 h-3 inline mr-1" />
          {t("trialBanner.selectPlan")}
        </button>
      </div>
    );
  }

  const now = new Date();
  const ends = new Date(trialEndsAt);
  const diffMs = ends.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

  if (daysLeft <= 0) {
    return (
      <div className="w-full bg-red-600 text-white text-center py-2 px-4 text-sm flex items-center justify-center gap-3 z-50">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{t("trialBanner.expired")}</span>
        <button
          onClick={() => setLocation("/settings")}
          className="ml-1 bg-white/20 hover:bg-white/30 text-white px-3 py-0.5 rounded text-xs font-semibold transition-colors"
        >
          <CreditCard className="w-3 h-3 inline mr-1" />
          {t("trialBanner.selectPlan")}
        </button>
        <a
          href={DEMO_BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-1 bg-white/10 hover:bg-white/20 text-white/80 px-3 py-0.5 rounded text-xs font-semibold transition-colors"
        >
          <Calendar className="w-3 h-3 inline mr-1" />
          {t("trialBanner.bookDemo")}
        </a>
      </div>
    );
  }

  return (
    <div className="w-full bg-[#1a1c22] border-b border-white/[0.08] text-white text-center py-2 px-4 text-sm flex items-center justify-center gap-3 z-50">
      <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
      <span>
        {t("trialBanner.remainingPrefix")}{" "}
        <span className={`font-bold ${daysLeft <= 3 ? "text-red-400" : "text-amber-400"}`}>
          {t("trialBanner.daysCount", { count: daysLeft })}
        </span>{" "}
        {t("trialBanner.remainingSuffix")}
      </span>
      <button
        onClick={() => setLocation("/settings")}
        className="ml-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 px-3 py-0.5 rounded text-xs font-semibold transition-colors"
      >
        <CreditCard className="w-3 h-3 inline mr-1" />
        {t("trialBanner.selectPlan")}
      </button>
      <a
        href={DEMO_BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-1 bg-white/10 hover:bg-white/20 text-white/50 hover:text-white/80 px-3 py-0.5 rounded text-xs font-semibold transition-colors"
      >
        <Calendar className="w-3 h-3 inline mr-1" />
        {t("trialBanner.bookDemo")}
      </a>
    </div>
  );
}
